import { Component, inject, OnDestroy, TemplateRef, ViewChild } from "@angular/core";
import { AbstractControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { MatDialog, MatDialogActions, MatDialogClose } from "@angular/material/dialog";
import { ActivatedRoute, ParamMap, Router } from "@angular/router";
import { FlexDirective, LayoutAlignDirective, LayoutDirective } from "@ngbracket/ngx-layout/flex";
import { ForgeButtonModule } from "@tylertech/forge-angular";
import { LgdFormControlFactory, LgdPasswordComponent } from "@tylertech/lgd-reactive-forms";
import { RouterHelpers } from "@tylertech/shared-module";
import { PasswordResetViewModel } from "@tylertech/tyler-api-types";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { isHttpResponseError, LoggingService, RouteInfoService, SecurityRoute, UserService } from "../../../services";
import { createNewPasswordValidator } from "../changePasswordForm";

@Component({
    selector: "reset-password-form",
    templateUrl: "./resetPasswordForm.tmpl.html",
    imports: [
        FlexDirective,
        LayoutAlignDirective,
        LayoutDirective,
        ForgeButtonModule,
        LgdPasswordComponent,
        MatDialogActions,
        MatDialogClose,
        ReactiveFormsModule
    ]
})
export class ResetPasswordFormComponent implements OnDestroy
{
    private readonly router = inject(Router);
    private readonly matDialog = inject(MatDialog);
    private readonly $routeInfoService = inject(RouteInfoService);
    private readonly $loggingService = inject(LoggingService);
    private readonly $userService = inject(UserService);

    @ViewChild("successMessage") readonly successTemplate: TemplateRef<unknown>;

    protected readonly destructor$ = new Subject<void>();

    private _errors: Record<string, string>;
    private key: string;

    readonly mainForm: FormGroup;
    error: string;

    constructor()
    {
        const activatedRoute = inject(ActivatedRoute);

        activatedRoute.fragment
            .pipe(takeUntil(this.destructor$))
            .subscribe((fragment: string | null) =>
            {
                this.key = fragment?.replace("key=", "");
            });

        const newPasswordControl = LgdFormControlFactory.text(undefined, true, {});
        const newPasswordConfirmControl = LgdFormControlFactory.text(undefined, true, {});
        this.mainForm = new FormGroup(
            {
                newPassword: newPasswordControl,
                newPasswordConfirm: newPasswordConfirmControl,
            },
            [
                createNewPasswordValidator(newPasswordControl, newPasswordConfirmControl)
            ]);
    }

    protected get newPasswordControl(): AbstractControl<string> { return this.mainForm.get("newPassword"); }
    protected get newPasswordConfirmControl(): AbstractControl<string> { return this.mainForm.get("newPasswordConfirm"); }

    get Errors(): Record<string, string>
    {
        return this._errors;
    }

    get tenant(): string
    {
        return this.$routeInfoService.tenant?.replace(/"/g, "");
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    onCancel(): void
    {
        this.router.navigate([SecurityRoute.Login]);
    }

    onSubmit(): Promise<void>
    {
        if (this.mainForm.invalid)
        {
            return Promise.reject("form is invalid");
        }

        const reset: PasswordResetViewModel = {
            Key: this.key,
            Tenant: this.tenant,
            Password: this.newPasswordControl.value,
        };
        return this.$userService.ResetPassword(reset)
            .then(
                () =>
                {
                    this.$loggingService.success("password reset successful");

                    const dialogRef = this.matDialog.open(this.successTemplate);
                    dialogRef.afterClosed()
                        .subscribe(() =>
                        {
                            // location.href = "/";
                            this.router.navigate([]);
                        });
                },
                (err: unknown) =>
                {
                    this.error = isHttpResponseError(err)
                        ? err.error.Message
                        : err?.toString();
                    this.$loggingService.info("result:", err);
                });
    }
}
